import { IonButton } from '@ionic/react';
import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../ui/Avatar';
import {
  useDeleteConnection,
  useMyConnections,
} from '../../hooks/useConnections';
import { notify } from '../../services/notify';

/**
 * Pending requests the caller has sent (`requests_from_me`). Three row shapes:
 *
 *  1. Known recipient (`connectedProfile` set) → their name. This covers both a
 *     request sent to an existing profile and a share link that's been opened
 *     but not yet accepted.
 *  2. Shell email invite (`invitedEmail` set, no profile yet) → the address.
 *  3. Share link nobody's opened yet (no recipient AND no email) → a generic
 *     "Share link / Not yet opened" row. These are minted every time the user
 *     taps "Share a connection link", so rather than show a blank "invitation
 *     pending" we label them and offer a re-share affordance (the link is
 *     reconstructable from the row id as `/c={id}`).
 *
 * Withdrawing any row is the DELETE path.
 *
 * Renders nothing when there are no pending sent requests.
 */
export const SentConnectionRequests: React.FC = () => {
  const { t } = useTranslation('connections');
  const { data: sent } = useMyConnections('requests_from_me');
  const withdraw = useDeleteConnection();

  if (!sent || sent.length === 0) return null;

  // Re-hand a share link to the OS share sheet (or clipboard fallback), mirroring
  // the mint-time flow in ConnectFriendsModal. The link is just `/c={id}`.
  const reshare = async (id: string) => {
    const url = `${window.location.origin}/c=${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: t('modal.linkShareTitle'), url });
      } else {
        await navigator.clipboard.writeText(url);
        notify.success(t('modal.copied'));
      }
    } catch {
      // Dismissed share sheet or unavailable clipboard — no-op.
    }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <h3 className="font-medium">{t('sent.title')}</h3>
      <ul className="divide-y divide-border">
        {sent.map((c) => {
          const recipient = c.connectedProfile;
          // A share link nobody has opened: no resolved recipient and no email
          // it was ever targeted at.
          const isShareLink = !recipient && !c.invitedEmail;
          // Known recipient → their name. Shell invite → the email we sent it
          // to. Unopened share link → a generic title (handled below).
          const name = recipient
            ? recipient.displayName || recipient.email || t('sent.someone')
            : c.invitedEmail || t('sent.shareLinkTitle');
          const subtitle = isShareLink
            ? t('sent.shareLinkPending')
            : t('sent.pending');
          const isWithdrawing =
            withdraw.isPending && withdraw.variables === c.id;
          return (
            // Row wraps as a whole. The identity keeps a text-relative floor
            // (min-w in rem, which grows with the OS/browser text size) so
            // that as text scales up the name+actions stop fitting and the
            // actions drop to their own line instead of squishing the name.
            <li key={c.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="flex min-w-[10rem] flex-1 items-center gap-3">
                {isShareLink ? (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-200">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                  </span>
                ) : (
                  <Avatar src={recipient?.avatarUrl} name={name} size="md" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{name}</p>
                  <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {isShareLink && (
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={() => reshare(c.id)}
                  >
                    {t('sent.reshare')}
                  </IonButton>
                )}
                <IonButton
                  size="small"
                  fill="outline"
                  color="medium"
                  onClick={() => withdraw.mutate(c.id)}
                  disabled={isWithdrawing}
                >
                  {t('sent.withdraw')}
                </IonButton>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
